import React, {
  useState,
  useEffect,
  useCallback,
  useReducer,
  useImperativeHandle,
  useRef,
  ForwardedRef,
  RefObject,
} from 'react';
import { styled, useTheme, useMediaQuery, Typography, Modal, Box, Slide } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Fund } from '../api/useFunds';
import { CashbackModel, DEFAULT_BIC } from '../utils/cashbackConstants';
import { Divider } from '../components/Divider';
import { PartnerBankSelector } from './PartnerBankSelector';
import { CashbackDisclaimer } from './CashbackDisclaimer';
import { FundInputWrapper } from './FundInput/FundInputWrapper';
import { FundEditModal } from './FundEditModal/FundEditModal';
import { ChartSection } from './ChartSection/ChartSection';
import { FundAutocompleteSection } from './FundAutocompleteSection/FundAutocompleteSection';

const FundsSection = styled('div')(({ theme }) => ({
  padding: theme.spacing(6, 5, 0),

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3, 4, 10),
  },
}));

const FundsWrapper = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(1),

  '& > div': {
    margin: theme.spacing(5, 0),
  },
  '& > div:first-of-type': {
    marginTop: 0,
  },
  '& > div:last-of-type': {
    marginBottom: 0,
  },

  [theme.breakpoints.down('md')]: {
    paddingTop: 0,

    '& > div': {
      margin: theme.spacing(3, 0),
    },
    '& > div:not(:first-of-type)': {
      marginBottom: theme.spacing(3),
    },
    '& > div:last-of-type': {
      marginBottom: theme.spacing(3),
    },
  },
}));

export interface CashbackAdvantage {
  cashback: number;
  totalPortfolioCommission: number;
  totalAgio: number;
  fee: number;
}

interface AddAction {
  type: 'add';
  payload: {
    fund: Fund;
    investedAmount?: number;
    addedExternally?: boolean;
  };
}

interface RemoveAction {
  type: 'remove';
  payload: {
    fund: Fund;
  };
}

interface ReplaceAction {
  type: 'replace';
  payload: Map<Fund, number>;
}

interface UpdateAction {
  type: 'update';
  payload: {
    fund: Fund;
    investedAmount: number;
  };
}

interface ClearAction {
  type: 'clear';
}

const reducer = (
  state: Map<Fund, number>,
  action: AddAction | RemoveAction | UpdateAction | ClearAction | ReplaceAction
) => {
  switch (action.type) {
    case 'add': {
      return !action.payload?.fund ||
        Array.from(state).find(([fund]) => fund.id === action.payload.fund?.id)
        ? state
        : new Map(
            state.set(
              action.payload.fund,
              action.payload.investedAmount ? Number(action.payload.investedAmount) : 10000
            )
          );
    }
    case 'remove': {
      state.delete(action.payload.fund);
      return new Map(state);
    }
    case 'update': {
      return state.has(action.payload.fund)
        ? new Map(state.set(action.payload.fund, Number(action.payload.investedAmount)))
        : state;
    }
    case 'replace': {
      return action.payload;
    }
    case 'clear': {
      return new Map<Fund, number>();
    }

    default:
      throw new Error('invalid dispatch state given');
  }
};

export interface Props {
  initialState?: Map<Fund, number>;
  initialBic?: Bic;
  onStateChange?: (state: Map<Fund, number>, bic: Bic) => void;
  onFundSelect?: (fund: Fund) => void;
  bookNowUrl: string;
  model?: CashbackModel;
}

export interface CalculatorState {
  addFund?: (fund: Fund) => void;
  calculatorRef?: RefObject<HTMLDivElement>;
  fundsMap?: Fund[];
}

export const DetailCalculator = React.forwardRef(
  (
    {
      initialState,
      initialBic,
      onStateChange,
      onFundSelect,
      bookNowUrl,
      model = 'flat-fee',
    }: Props,
    ref: ForwardedRef<CalculatorState>
  ) => {
    const calculatorRef = useRef<HTMLDivElement>(null);
    const [fundToEdit, setFundToEdit] = useState<Fund | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [addedExternally, setAddedExternally] = useState<string[]>([]);
    const [fundsMap, dispatch] = useReducer(reducer, new Map());
    const [isLoaded, setIsLoaded] = useState(false);
    const [bic, setBic] = useState<Bic>(DEFAULT_BIC);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
    const { t } = useTranslation();

    useEffect(() => {
      if (onStateChange && isLoaded) {
        onStateChange(fundsMap, bic);
      }
    }, [fundsMap, bic]);

    // this is to make localStorage changes out of next environment
    useEffect(() => {
      if (initialBic) {
        setBic(initialBic);
        setIsLoaded(true);
      }
      if (initialState) {
        dispatch({ type: 'replace', payload: initialState });
        setIsLoaded(true);
      }
    }, [initialBic, initialState]);

    const handlePartnerBankChange = (bic: Bic) => {
      setBic(bic);
      onStateChange?.(fundsMap, bic);
    };

    const handleFundSelection = (fund: Fund) => {
      if (onFundSelect) {
        onFundSelect(fund);
      }
      dispatch({ type: 'add', payload: { fund: fund } });
    };

    const addFund = (fund: Fund) => {
      setAddedExternally(
        addedExternally.includes(`${fund.isin}`)
          ? addedExternally
          : [...addedExternally, `${fund.isin}`]
      );
      dispatch({ type: 'add', payload: { fund: fund } });
    };

    const onFundEditModalClose = () => {
      setFundToEdit(null);
      setIsEditModalOpen(false);
    };

    const handleFundRemoval = (fund: Fund) => {
      dispatch({ type: 'remove', payload: { fund: fund } });
    };

    const handleInvestedAmountChange = (fund: Fund, investedAmount: number) => {
      dispatch({
        type: 'update',
        payload: { fund: fund, investedAmount: investedAmount },
      });
    };

    const handleBarChartClick = useCallback(
      (bic: Bic) => {
        setBic(bic);
        onStateChange?.(fundsMap, bic);
      },
      [setBic]
    );

    // this is to expose methods to the outer space
    useImperativeHandle(
      ref,
      (): CalculatorState => ({
        addFund,
        calculatorRef,
        fundsMap: Array.from(fundsMap.keys()),
      }),
      [calculatorRef, fundsMap]
    );

    return (
      <div ref={calculatorRef}>
        <PartnerBankSelector onChange={handlePartnerBankChange} bic={bic} />
        <Divider />

        <FundsSection>
          <Typography color="textSecondary" component="label" variant="body1" id="funds-list-label">
            {t('cashbackCalculator.detailed.funds.label')}
          </Typography>

          {fundsMap.size > 0 && (
            <FundsWrapper>
              {Array.from(fundsMap).map(([fund, investedAmount], index) => {
                return (
                  <Box
                    key={`fund-input-${fund.isin}`}
                    data-testid={`fund-input-${fund.isin}`}
                    id={`fund-input-${fund.isin}`}
                  >
                    {index > 0 && <Divider />}
                    <FundInputWrapper
                      bic={bic}
                      fund={fund}
                      isAddedExternally={addedExternally.includes(`${fund.isin}`)}
                      isCollapsed={isMobile && fundsMap.size - 1 !== index}
                      investedAmount={investedAmount}
                      onChange={handleInvestedAmountChange}
                      onRemove={handleFundRemoval}
                      onEdit={(fund: Fund) => {
                        setFundToEdit(fund);
                        setIsEditModalOpen(true);
                      }}
                    />
                  </Box>
                );
              })}
            </FundsWrapper>
          )}
          <FundAutocompleteSection
            fundsMap={fundsMap}
            onClearAll={() => {
              dispatch({ type: 'clear' });
            }}
            onFundSelect={handleFundSelection}
            onAddFundClick={() => setIsEditModalOpen(true)}
          />
        </FundsSection>

        <ChartSection
          bookNowUrl={bookNowUrl}
          fundsMap={fundsMap}
          bic={bic}
          onChartBarClick={handleBarChartClick}
          model={model}
        />
        <Divider />
        <CashbackDisclaimer
          fundsMap={fundsMap}
          bic={bic}
          model={model}
          onChartBarClick={handleBarChartClick}
        />
        <Modal disablePortal open={isEditModalOpen} onClose={onFundEditModalClose}>
          <Slide direction="up" in={isEditModalOpen}>
            <Box
              data-testid="fund-edit-modal"
              sx={{
                position: 'fixed',
                bottom: 0,
                maxHeight: '100%',
                outline: 'none',
                overflowY: 'auto',
                width: '100%',
              }}
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                !(event.target as HTMLDivElement).closest('.fund-modal-inner-container') &&
                  onFundEditModalClose();
              }}
            >
              <FundEditModal
                fund={fundToEdit}
                bic={bic}
                investedAmount={fundsMap.get(fundToEdit || ({} as Fund))}
                onApply={(fund, amount) => {
                  dispatch({
                    type: fundToEdit ? 'update' : 'add',
                    payload: { fund, investedAmount: amount },
                  });
                  setIsEditModalOpen(false);
                }}
                onCancel={() => {
                  setFundToEdit(null);
                  setIsEditModalOpen(false);
                }}
              />
            </Box>
          </Slide>
        </Modal>
      </div>
    );
  }
);

export default DetailCalculator;
